/**
 * AdvancedActions - executes advanced (async) action scripts via the sandboxed executor.
 */

import { Action } from '../models/Action';
import { Command } from '../models/Command';
import { Globals } from '../core/Globals';
import { gameController } from '../core/GameController';
import { Settings } from '../core/Settings';
import { GameCommands } from './GameCommands';
import { Finder } from '../utils/Finder';
import { PerformTextReplacements } from '../utils/TextHandler';
import { evalAdvancedScriptAsync, evalAdvancedGlobalScripts, evalJankyJavascriptAsync, GetArrayIndex } from '../utils/Utilities';

function advancedDebugLog(...args: any[]): void {
	if (!(Settings as any).debugEnabled) return;
	// eslint-disable-next-line no-console
	console.debug('[AdvancedAction]', ...args);
}

type AdvancedContext = {
	id: number;
	action: Action;
	objectBeingActedUpon: any;
};

type RpcRequest = {
	method: string;
	args: any[];
	context?: { id?: number };
};

let nextContextId = 1;
const contexts = new Map<number, AdvancedContext>();

function createCommand(commandType: string, payload: any): Command {
	const cmd = new Command();
	cmd.cmdtype = commandType;
	cmd.CommandName = payload?.commandName || '';
	cmd.CommandText = payload?.param1 || '';
	cmd.CommandPart2 = payload?.param2 || '';
	cmd.CommandPart3 = payload?.param3 || '';
	cmd.CommandPart4 = payload?.param4 || '';
	cmd.CustomChoices = Array.isArray(payload?.customChoices) ? payload.customChoices.slice() : [];
	cmd.EnhInputData = payload?.enhInputData || null;
	cmd.AllowHtml = Boolean(payload?.allowHtml);
	(cmd as any).NoParagraphWrapper = Boolean(payload?.noParagraphWrapper || payload?.suppressParagraph || payload?.rawHtml);
	(cmd as any).IsAdvanced = true;
	return cmd;
}

function parseVariableName(name: string): { baseName: string; index: number | string; index2: number | string; isArraySet: boolean } {
	let baseName = String(name || '');
	let isArraySet = false;
	const arrayIndex = baseName.indexOf('Array:');
	if (arrayIndex > -1) {
		isArraySet = true;
		baseName = baseName.substring(arrayIndex + 'Array:'.length);
	}
	const index = GetArrayIndex(baseName, 0);
	const index2 = GetArrayIndex(baseName, 1);
	if (baseName.indexOf('(') > -1) {
		baseName = baseName.substring(0, baseName.indexOf('('));
	}
	return { baseName, index, index2, isArraySet };
}

function clampNumericValue(variable: any, value: number): number {
	if (!variable?.bEnforceRestrictions) return value;
	const minRaw = PerformTextReplacements(variable.dMin ?? '', null);
	const maxRaw = PerformTextReplacements(variable.dMax ?? '', null);
	const min = parseFloat(minRaw);
	const max = parseFloat(maxRaw);
	let next = value;
	if (!Number.isNaN(min) && next < min) next = min;
	if (!Number.isNaN(max) && next > max) next = max;
	return next;
}

function setVariableValue(variable: any, value: any, index: number | string, index2: number | string, isArraySet: boolean): void {
	if (!variable) return;
	const cheatFreezes = (window as any).cheatFreezes;
	if (cheatFreezes?.variables?.[variable.varname]) return;
	const type = String(variable.vartype || '').toUpperCase();
	const isArray = type.endsWith('ARRAY');
	if (isArray) {
		if (isArraySet) {
			if (!Array.isArray(value)) {
				throw new Error('Value must be an array for array variables.');
			}
			variable.VarArray = value;
			return;
		}
		const idx = typeof index === 'number' ? index : parseInt(String(index), 10);
		const idx2 = typeof index2 === 'number' ? index2 : parseInt(String(index2), 10);
		if (idx === -1) {
			if (!Array.isArray(value)) {
				throw new Error('Value must be an array for array variables.');
			}
			variable.VarArray = value;
			return;
		}
		if (!Array.isArray(variable.VarArray)) variable.VarArray = [];
		if (idx2 !== -1) {
			if (!Array.isArray(variable.VarArray[idx])) variable.VarArray[idx] = [];
			variable.VarArray[idx][idx2] = value;
		} else {
			variable.VarArray[idx] = value;
		}
		return;
	}
	if (type.includes('NUMBER')) {
		const num = Number(value);
		const clamped = Number.isNaN(num) ? num : clampNumericValue(variable, num);
		variable.dNumType = clamped;
		return;
	}
	if (type.includes('DATETIME')) {
		const DateTimes = (window as any).DateTimes;
		if (value instanceof Date && DateTimes?.formatDateString) {
			variable.dtDateTime = DateTimes.formatDateString(value, DateTimes.defaultDateFormat);
		} else {
			variable.dtDateTime = value != null ? String(value) : '';
		}
		return;
	}
	variable.sString = value != null ? String(value) : '';
}

function getVariableValue(variable: any, index: number | string, index2: number | string): any {
	if (!variable) return null;
	const type = String(variable.vartype || '').toUpperCase();
	if (type.endsWith('ARRAY')) {
		const idx = typeof index === 'number' ? index : parseInt(String(index), 10);
		const idx2 = typeof index2 === 'number' ? index2 : parseInt(String(index2), 10);
		if (idx === -1) return Array.isArray(variable.VarArray) ? variable.VarArray : [];
		if (!Array.isArray(variable.VarArray)) return null;
		if (idx2 !== -1) return variable.VarArray?.[idx]?.[idx2];
		return variable.VarArray?.[idx];
	}
	if (type.includes('NUMBER')) return variable.dNumType;
	if (type.includes('DATETIME')) {
		const DateTimes = (window as any).DateTimes;
		if (DateTimes?.stringDateToDate) {
			return DateTimes.stringDateToDate(variable.dtDateTime || '');
		}
		return variable.dtDateTime;
	}
	return variable.sString;
}

function compareDateTimeValue(actual: any, operator: string, expected: any): boolean {
	const DateTimes = (window as any).DateTimes;
	const moment = (window as any).moment;
	const normalizeComparison = (comparison: string): string => {
		return comparison
			.replace(/^Hour Is\s+/i, '')
			.replace(/^Is\s+/i, '')
			.trim();
	};
	const comparison = normalizeComparison(String(operator || 'Equals'));
	const expectedText = expected != null ? String(expected).trim() : '';
	const isNumeric = expectedText.length > 0 && !Number.isNaN(parseFloat(expectedText));
	const hasDateChars = /[\/\-:T]/.test(expectedText) || /am|pm/i.test(expectedText);

	const parseMoment = (value: any): any => {
		if (moment && DateTimes?.defaultDateFormat) {
			if (value instanceof Date) return moment(value);
			const strict = moment(String(value || ''), DateTimes.defaultDateFormat, true);
			if (strict && strict.isValid && strict.isValid()) return strict;
		}
		return moment ? moment(value) : null;
	};

	const dateMoment = parseMoment(actual);
	if (!dateMoment || !dateMoment.isValid || !dateMoment.isValid()) return false;

	if (hasDateChars) {
		const compareMoment = parseMoment(expectedText);
		if (!compareMoment || !compareMoment.isValid || !compareMoment.isValid()) return false;
		const left = dateMoment.valueOf();
		const right = compareMoment.valueOf();
		if (comparison == 'Equals') return left == right;
		if (comparison == 'Not Equals') return left != right;
		if (comparison == 'Greater Than') return left > right;
		if (comparison == 'Greater Than or Equals') return left >= right;
		if (comparison == 'Less Than') return left < right;
		if (comparison == 'Less Than or Equals') return left <= right;
		return false;
	}

	if (isNumeric) {
		const hourValue = dateMoment.hour();
		const compareValue = parseFloat(expectedText);
		if (comparison == 'Equals') return hourValue == compareValue;
		if (comparison == 'Not Equals') return hourValue != compareValue;
		if (comparison == 'Greater Than') return hourValue > compareValue;
		if (comparison == 'Greater Than or Equals') return hourValue >= compareValue;
		if (comparison == 'Less Than') return hourValue < compareValue;
		if (comparison == 'Less Than or Equals') return hourValue <= compareValue;
		return false;
	}

	const formatted = DateTimes?.formatDateString && dateMoment?.toDate
		? DateTimes.formatDateString(dateMoment.toDate(), DateTimes.defaultDateFormat)
		: String(actual ?? '');
	if (comparison == 'Equals') return formatted == expectedText;
	if (comparison == 'Not Equals') return formatted != expectedText;
	if (comparison == 'Greater Than') return formatted > expectedText;
	if (comparison == 'Less Than') return formatted < expectedText;
	return false;
}

function cloneForWorker<T>(value: T): T {
	if (value instanceof Date) {
		return value;
	}
	// RAGS entities routinely have back-references (e.g. an Action's `_owner`
	// pointing to the Character/Room/Object it belongs to), which makes a
	// naive JSON.stringify throw "Converting circular structure to JSON" and,
	// before this fix, discarded the ENTIRE value (falling back to null) —
	// e.g. getCharacterByName("Carmen") would come back as null just because
	// one of its Actions referenced it back, breaking every script that
	// touched that character. Break cycles instead of giving up entirely: the
	// replacer drops (returns undefined for) any object already seen anywhere
	// else in this value's tree, same trade-off structuredClone/JSON.stringify
	// circular-safe helpers commonly make (a shared-but-non-circular
	// reference the second time it's seen is also dropped) — acceptable here
	// since scripts read entity data, they don't rely on shared object
	// identity across nested fields.
	const seen = new WeakSet<object>();
	try {
		const json = JSON.stringify(value, (_key, val) => {
			if (val && typeof val === 'object') {
				if (seen.has(val)) return undefined;
				seen.add(val);
			}
			return val;
		});
		return json === undefined ? (null as unknown as T) : JSON.parse(json);
	} catch (err) {
		// Only reachable now for genuinely non-serializable values the
		// replacer above can't help with. Returning `value` as-is here used to
		// hand a live, unclonable object straight to postMessage, which fails
		// with an opaque DataCloneError far from this call site (e.g. "getSelf
		// DataCloneError: ... could not be cloned", with no indication of
		// WHICH RPC call or value caused it). Log here, at the actual source,
		// so a future case like that is immediately diagnosable instead of a
		// mystery postMessage crash.
		console.error('[AdvancedAction] cloneForWorker: value is not serializable for the worker/iframe boundary, returning null instead of a live reference that would crash postMessage. Value:', value, err);
		return null as unknown as T;
	}
}

function detectSelfType(value: any): string | null {
	if (!value || typeof value !== 'object') return null;
	if (typeof value.Charname === 'string') return 'character';
	if (typeof value.UniqueIdentifier === 'string') return 'object';
	if (typeof value.UniqueID === 'string') return 'room';
	if (typeof value.TType === 'string' || typeof value.TimerSeconds === 'number') return 'timer';
	if (typeof value.PlayerGender === 'string' || typeof value.StartingRoom === 'string') return 'player';
	return null;
}

async function runCommand(commandType: string, payload: any): Promise<{ stop?: boolean }> {
	advancedDebugLog('command', commandType, payload);
	const cmd = createCommand(commandType, payload || {});
	const loopObj = Globals.loopArgs?.object;
	const part2 = PerformTextReplacements(cmd.CommandPart2, loopObj);
	const part3 = PerformTextReplacements(cmd.CommandPart3, loopObj);
	const part4 = PerformTextReplacements(cmd.CommandPart4, loopObj);
	const cmdtxt = PerformTextReplacements(cmd.CommandText, loopObj);
	advancedDebugLog('command-ready', commandType, {
		part2,
		part3,
		part4,
		cmdtxt,
	});
	const expectedLen = (Globals as any).length;
	const globalsIndex = Globals.store({
		objectBeingActedUpon: (Globals as any).objectBeingActedUpon,
		actionBeingTaken: (Globals as any).actionBeingTaken,
	});
	let ran = false;
	try {
		await GameCommands.runCommandsAsync([cmd]);
		ran = true;
		if (gameController.gameAwaitingInput) {
			await gameController.stopAwaitingInputAsync(false);
		}
		const stopped = Boolean(gameController.gamePaused);
		advancedDebugLog('command-done', commandType, { stop: stopped });
		return stopped ? { stop: true } : {};
	} finally {
		try {
			if ((window as any).__REGALIA_DEBUG_GLOBALS) console.debug('[AdvancedAction] restoring globals from runCommand', globalsIndex, 'ran:', ran, 'expectedLen:', expectedLen, 'currentLen:', (Globals as any).length);
		} catch (e) {}
		// Restore non-indexed: pop any frames pushed after we started this call
		try {
			while ((Globals as any).length > expectedLen) {
				(Globals as any).restore();
			}
		} catch (e) {
			console.error('[AdvancedAction] error restoring globals after runCommand', e);
		}
	}
}

function getContext(contextId?: number): AdvancedContext | undefined {
	if (!contextId) return undefined;
	return contexts.get(contextId);
}

function requestInputFromPlayer(options: any): Promise<void> {
	const inputType = String(options?.type || options?.inputType || 'Text');
	const prompt = String(options?.prompt || options?.title || '');
	const choices = Array.isArray(options?.choices) ? options.choices.slice() : [];
	const allowCancel = Boolean(options?.allowCancel);

	const cmd = createCommand('CT_REGALIA_ADVANCED_INPUT', { param2: inputType, param4: prompt, customChoices: choices });
	cmd.CustomChoices = choices;
	(cmd as any).EnhInputData = { bAllowCancel: allowCancel };

	return new Promise<void>((resolve) => {
		(Globals as any).advancedInputResolver = resolve;
		(Globals as any).advancedInputReject = undefined;
		Globals.variableGettingSet = cmd;

		const GameUI = (window as any).GameUI;
		if (inputType === 'Custom') {
			GameUI?.setCmdInputForCustomChoices?.(prompt, cmd);
			gameController.startAwaitingInputAsync().then(resolve);
			return;
		}
		if (inputType === 'Text') {
			GameUI?.showTextMenuChoice?.(prompt);
			gameController.startAwaitingInputAsync().then(resolve);
			return;
		}

		GameUI?.clearCmdInputChoices?.();
		if (inputType === 'Character' || inputType === 'Characters') {
			GameUI?.addCharacterOptions?.();
		} else if (inputType === 'Object' || inputType === 'Objects') {
			GameUI?.addObjectOptions?.();
		} else if (inputType === 'Inventory') {
			const objectContainsObject = (window as any).objectContainsObject;
			const objecttostring = (window as any).objecttostring;
			function addChildObjects(parentObject: any) {
				if (!parentObject?.bContainer) return;
				const objects = (window as any).TheGame?.Objects || [];
				objects.forEach((innerObject: any) => {
					if (objectContainsObject?.(parentObject, innerObject)) {
						GameUI?.addCmdInputChoice?.((window as any).objecttostring?.(innerObject) || innerObject?.name || '', innerObject);
						addChildObjects(innerObject);
					}
				});
			}
			const objects = (window as any).TheGame?.Objects || [];
			objects.forEach((obj: any) => {
				if (obj.locationtype == 'LT_PLAYER') {
					GameUI?.addCmdInputChoice?.((window as any).objecttostring?.(obj) || obj?.name || '', obj);
					addChildObjects(obj);
				}
			});
		} else if (inputType === 'ObjectOrCharacter') {
			GameUI?.addObjectOptions?.();
			GameUI?.addCharacterOptions?.();
		}

		GameUI?.setCmdInputMenuTitle?.((Globals as any).findActionBeingTaken(), prompt);
		gameController.startAwaitingInputAsync().then(resolve);
	});
}

async function handleRpc(request: RpcRequest): Promise<any> {
	let method: any = request.method;
	let args: any[] = request.args || [];
	let context = request.context;
	if (method && typeof method === 'object' && (method as any).method) {
		const wrapped = method as any;
		method = wrapped.method;
		args = wrapped.args || [];
		context = wrapped.context;
	}
	const ctx = getContext(context?.id);
	advancedDebugLog('rpc', method, args, ctx?.action?.name || '');

	switch (method) {
		case 'command': {
			const commandType = String(args[0] || '');
			const payload = args[1] || {};
			return runCommand(commandType, payload);
		}
		case 'getAdditionalData': {
			return (Globals as any).additionalData;
		}
		case 'getObject': {
			return cloneForWorker(Finder.object(String(args[0] || '')) || null);
		}
		case 'getAllObjects': {
			return cloneForWorker((window as any).TheGame?.Objects || []);
		}
		case 'getCurrentObject': {
			return cloneForWorker(ctx?.objectBeingActedUpon || null);
		}
		case 'getRoom': {
			return cloneForWorker(Finder.room(String(args[0] || '')) || null);
		}
		case 'getAllRooms': {
			return cloneForWorker((window as any).TheGame?.Rooms || []);
		}
		case 'getRoomExits': {
			const room = Finder.room(String(args[0] || ''));
			return cloneForWorker(room?.Exits || []);
		}
		case 'getCurrentRoom': {
			const currentId = (window as any).TheGame?.Player?.CurrentRoom || '';
			return cloneForWorker(Finder.room(currentId) || null);
		}
		case 'getSelf': {
			const selfValue = ctx?.objectBeingActedUpon || null;
			return {
				type: detectSelfType(selfValue),
				value: cloneForWorker(selfValue),
			};
		}
		case 'getCharacter': {
			return cloneForWorker(Finder.character(String(args[0] || '')) || null);
		}
		case 'getAllCharacters': {
			return cloneForWorker((window as any).TheGame?.Characters || []);
		}
		case 'getVariable': {
			const parsed = parseVariableName(String(args[0] || ''));
			return cloneForWorker(Finder.variable(parsed.baseName) || null);
		}
		case 'getVariableValue': {
			const parsed = parseVariableName(String(args[0] || ''));
			const variable = Finder.variable(parsed.baseName);
			return cloneForWorker(getVariableValue(variable, parsed.index, parsed.index2));
		}
		case 'getTimer': {
			return cloneForWorker(Finder.timer(String(args[0] || '')) || null);
		}
		case 'getItemsInRoom': {
			const roomId = String(args[0] || '');
			const objects = (window as any).TheGame?.Objects || [];
			return cloneForWorker(objects.filter((item: any) => item.locationtype === 'LT_ROOM' && item.locationname === roomId));
		}
		case 'getItemsInObject': {
			const objectId = String(args[0] || '');
			const objects = (window as any).TheGame?.Objects || [];
			return cloneForWorker(objects.filter((item: any) => item.locationtype === 'LT_IN_OBJECT' && item.locationname === objectId));
		}
		case 'getItemsInInventory': {
			const objects = (window as any).TheGame?.Objects || [];
			return cloneForWorker(objects.filter((item: any) => item.locationtype === 'LT_PLAYER'));
		}
		case 'getItemsInCharacterInventory': {
			const charName = String(args[0] || '');
			const objects = (window as any).TheGame?.Objects || [];
			return cloneForWorker(objects.filter((item: any) => item.locationtype === 'LT_CHARACTER' && item.locationname === charName));
		}
		case 'getItemsInGroup': {
			const groupName = String(args[0] || '');
			return cloneForWorker(Finder.objectGroup(groupName) as any || []);
		}
		case 'getRoomsInGroup': {
			const groupName = String(args[0] || '');
			return cloneForWorker(Finder.roomGroup(groupName) as any || []);
		}
		case 'getCharactersInGroup': {
			const groupName = String(args[0] || '');
			return cloneForWorker(Finder.characterGroup(groupName) as any || []);
		}
		case 'getVariablesInGroup': {
			const groupName = String(args[0] || '');
			return cloneForWorker(Finder.variableGroup(groupName) as any || []);
		}
		case 'getTimersInGroup': {
			const groupName = String(args[0] || '');
			return cloneForWorker(Finder.timerGroup(groupName) as any || []);
		}
		case 'getPlayer': {
			return cloneForWorker(cloneForWorker((window as any).TheGame?.Player || null));
		}
		case 'getGameInfo': {
			const game = (window as any).TheGame;
			if (!game) return null;
			return cloneForWorker({
				Title: game.Title || '',
				AuthorName: game.AuthorName || '',
				GameVersion: game.GameVersion || '',
				GameInformation: game.GameInformation || '',
				OpeningMessage: game.OpeningMessage || '',
			});
		}
		case 'setVariable': {
			const name = String(args[0] || '');
			const value = args[1];
			const parsed = parseVariableName(name);
			const variable = Finder.variable(parsed.baseName);
			if (!variable) return null;
			setVariableValue(variable, value, parsed.index, parsed.index2, parsed.isArraySet);
			return cloneForWorker(variable);
		}
		case 'compareDateTime': {
			const actual = args[0];
			const operator = String(args[1] || '');
			const expected = args[2];
			return compareDateTimeValue(actual, operator, expected);
		}
		case 'applyDateTimeOperation': {
			const name = String(args[0] || '');
			const op = String(args[1] || '');
			const rawValue = args[2];
			const parsed = parseVariableName(name);
			const variable = Finder.variable(parsed.baseName);
			if (!variable) return null;
			const DateTimes = (window as any).DateTimes;
			const moment = (window as any).moment;
			const parseMoment = (value: any): any => {
				if (moment && DateTimes?.defaultDateFormat) {
					if (value instanceof Date) return moment(value);
					const strict = moment(String(value || ''), DateTimes.defaultDateFormat, true);
					if (strict && strict.isValid && strict.isValid()) return strict;
				}
				return moment ? moment(value) : null;
			};
			const dateMoment = parseMoment(variable.dtDateTime || '');
			if (!dateMoment || !dateMoment.isValid || !dateMoment.isValid()) return null;
			const valueText = rawValue instanceof Date && DateTimes?.formatDateString
				? DateTimes.formatDateString(rawValue, DateTimes.defaultDateFormat)
				: String(rawValue ?? '');
			switch (op) {
				case 'Add Days':
					dateMoment.add(valueText, 'day');
					break;
				case 'Add Hours':
					dateMoment.add(valueText, 'hour');
					break;
				case 'Add Minutes':
					dateMoment.add(valueText, 'minute');
					break;
				case 'Add Seconds':
					dateMoment.add(valueText, 'second');
					break;
				case 'Subtract Days':
					dateMoment.subtract(valueText, 'day');
					break;
				case 'Subtract Hours':
					dateMoment.subtract(valueText, 'hour');
					break;
				case 'Subtract Minutes':
					dateMoment.subtract(valueText, 'minute');
					break;
				case 'Subtract Seconds':
					dateMoment.subtract(valueText, 'second');
					break;
				case 'Set Day Of Month To':
					dateMoment.date(parseInt(valueText, 10));
					break;
				case 'Set Hours To':
					dateMoment.hour(parseInt(valueText, 10));
					break;
				case 'Set Minutes To':
					dateMoment.minute(parseInt(valueText, 10));
					break;
				case 'Set Seconds To':
					dateMoment.second(parseInt(valueText, 10));
					break;
				case 'Equals': {
					const nextMoment = parseMoment(valueText);
					if (nextMoment && nextMoment.isValid && nextMoment.isValid()) {
						variable.dtDateTime = nextMoment.format(DateTimes.defaultDateFormat);
						return cloneForWorker(variable.dtDateTime);
					}
					break;
				}
				default:
					break;
			}
			if (DateTimes?.formatDateString) {
				variable.dtDateTime = DateTimes.formatDateString(dateMoment.toDate(), DateTimes.defaultDateFormat);
			} else {
				variable.dtDateTime = dateMoment.toDate().toString();
			}
			return cloneForWorker(variable.dtDateTime);
		}
		case 'getMovingDirection': {
			return String(Globals.movingDirection || '');
		}
		case 'getMediaGroup': {
			const groupName = String(args[0] || '');
			const images = Finder.imageGroup(groupName) || [];
			return cloneForWorker(images.map((img: any) => img.TheName || img.filename || img.name).filter(Boolean));
		}
		case 'expandText': {
			const loopObj = Globals.loopArgs?.object || ctx?.objectBeingActedUpon || null;
			return PerformTextReplacements(String(args[0] ?? ''), loopObj);
		}
		case 'evaluateLegacyScript': {
			const script = String(args[0] ?? '');
			if (!script.trim()) return undefined;
			return evalJankyJavascriptAsync(script);
		}
		case 'getMediaList': {
			const game = (window as any).TheGame;
			if (!game || !Array.isArray(game.Images)) return [];
			return cloneForWorker(game.Images.map((img: any) => img.TheName || img.filename || img.name).filter(Boolean));
		}
		case 'requestInput': {
			const options = args[0] || {};
			return requestInputFromPlayer(options);
		}
		case 'log': {
			// eslint-disable-next-line no-console
			console.log('[AdvancedAction]', ...args);
			return null;
		}
		default:
			return null;
	}
}

// A SyntaxError thrown by `new AsyncFunction(...)` (how advanced scripts are
// actually run, inside the worker/iframe) carries NO line/column information
// at all in this engine (confirmed: V8 gives `err.stack` with no <anonymous>
// position for Function-constructor syntax errors, unlike some other error
// classes) — so a real syntax error in a script shows up as a bare "Invalid
// or unexpected token" with nothing to act on. The single most common way an
// AI-authored/hand-written script ends up with a real syntax error in
// practice is pasting multi-paragraph narrative text directly into a
// single/double-quoted string (only backtick template literals may contain a
// literal line break; "..."/'...' must use \n). Scan for exactly that
// specific, well-defined mistake up front — cheaply, on the main thread,
// before ever sending the script to the worker/iframe — so this common case
// gets an actionable line number instead of an opaque runtime crash. This is
// NOT a full parser and won't catch every possible syntax error; it targets
// the one failure mode that's actually been observed to matter here.
function findRawNewlineInsideStringLine(code: string): number | null {
	let state: 'code' | 'string' | 'template' | 'lineComment' | 'blockComment' = 'code';
	let quote = '';
	let line = 1;
	for (let i = 0; i < code.length; i++) {
		const ch = code[i];
		const next = code[i + 1];
		if (ch === '\n') {
			if (state === 'string') return line;
			line++;
			if (state === 'lineComment') state = 'code';
			continue;
		}
		if (state === 'code') {
			if (ch === '"' || ch === '\'') { state = 'string'; quote = ch; continue; }
			if (ch === '`') { state = 'template'; continue; }
			if (ch === '/' && next === '/') { state = 'lineComment'; i++; continue; }
			if (ch === '/' && next === '*') { state = 'blockComment'; i++; continue; }
			continue;
		}
		if (state === 'string') {
			if (ch === '\\') { i++; continue; }
			if (ch === quote) { state = 'code'; }
			continue;
		}
		if (state === 'template') {
			if (ch === '\\') { i++; continue; }
			if (ch === '`') { state = 'code'; }
			continue;
		}
		if (state === 'blockComment') {
			if (ch === '*' && next === '/') { state = 'code'; i++; }
			continue;
		}
	}
	return null;
}

export const AdvancedActions = {
	executeAdvancedAction: async function (action: Action, objectBeingActedUpon: any): Promise<void> {
		const script = action.AdvancedScript || '';
		if (script.trim().length === 0) return;

		const rawNewlineLine = findRawNewlineInsideStringLine(script);
		if (rawNewlineLine !== null) {
			const badLine = script.split('\n')[rawNewlineLine - 1] || '';
			console.error(`[AdvancedAction] Script "${action.name || ''}" has a syntax error at line ${rawNewlineLine}: a "..."/'...' string contains a literal line break, which is not valid JavaScript (only backtick template literals may span multiple lines). Use \\n inside the string instead. Offending line:`, badLine);
		}

		const contextId = nextContextId++;
		advancedDebugLog('execute', action.name || '', { contextId });
		contexts.set(contextId, { id: contextId, action, objectBeingActedUpon });
		Globals.advancedScriptDepth += 1;

		try {
			await evalAdvancedScriptAsync(script, { id: contextId }, handleRpc);
		} catch (err: any) {
			if (err && err.name === 'RAGS_STOP') {
				return;
			}
			throw err;
		} finally {
			contexts.delete(contextId);
			Globals.advancedScriptDepth = Math.max(0, Globals.advancedScriptDepth - 1);
			if (Globals.advancedScriptDepth === 0) {
				gameController.notifyAdvancedScriptsDrained();
			}
	}
	},
	executeAdvancedScriptText: async function (script: string, options?: { name?: string; self?: any }): Promise<void> {
		const trimmed = String(script || '').trim();
		if (!trimmed) return;
		const action = new Action();
		action.name = options?.name || 'Script';
		action.AdvancedScript = trimmed;
		const contextId = nextContextId++;
		contexts.set(contextId, { id: contextId, action, objectBeingActedUpon: options?.self ?? null });
		Globals.advancedScriptDepth += 1;
		try {
			await evalAdvancedScriptAsync(trimmed, { id: contextId }, handleRpc);
		} catch (err) {
			console.log('[AdvancedAction] Script error', err);
		} finally {
			contexts.delete(contextId);
			Globals.advancedScriptDepth = Math.max(0, Globals.advancedScriptDepth - 1);
			if (Globals.advancedScriptDepth === 0) {
				gameController.notifyAdvancedScriptsDrained();
			}
	}
	},
	executeAdvancedGlobalScripts: async function (scripts: string[]): Promise<void> {
		if (!Array.isArray(scripts) || scripts.length === 0) return;
		try {
			await evalAdvancedGlobalScripts(scripts, { id: 0 }, handleRpc);
		} catch (err) {
			console.log('[AdvancedAction] Global script error', err);
		}
	}
};

// Make available globally
(window as any).AdvancedActions = AdvancedActions;
